export const SUPPORTED_LOCALES = ['de-DE', 'en-US'] as const

export const TRANSLATION_NAMESPACES = ['pages', 'components', 'errors', 'validation', 'terminology'] as const

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]
export type TranslationNamespace = (typeof TRANSLATION_NAMESPACES)[number]

const LANGUAGE_TO_SUPPORTED_LOCALE = {
    de: 'de-DE',
    en: 'en-US',
} as const satisfies Record<'de' | 'en', SupportedLocale>

export const DEFAULT_LOCALE: SupportedLocale = 'de-DE'

export function isSupportedLocale(locale: string): locale is SupportedLocale {
    return SUPPORTED_LOCALES.includes(locale as SupportedLocale)
}

export function normalizeLocale(locale?: string | null): SupportedLocale | undefined {
    if (!locale) {
        return undefined
    }

    const sanitizedLocale = locale.toLowerCase().replace(/_/g, '-')
    const [language] = sanitizedLocale.split('-')
    return language && language in LANGUAGE_TO_SUPPORTED_LOCALE ? LANGUAGE_TO_SUPPORTED_LOCALE[language as 'de' | 'en'] : undefined
}

export function getBrowserLocale(): SupportedLocale | undefined {
    const browserLocales = typeof navigator === 'undefined' ? [] : [navigator.language, ...(navigator.languages || [])]

    for (const locale of browserLocales) {
        const normalizedLocale = normalizeLocale(locale)
        if (normalizedLocale) {
            return normalizedLocale
        }
    }

    return undefined
}

export function resolveLocale({
    userLocale,
    browserLocale,
}: {
    userLocale?: string | null
    browserLocale?: string | null
}): SupportedLocale {
    return normalizeLocale(userLocale) || normalizeLocale(browserLocale) || DEFAULT_LOCALE
}
